import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import apiInstance from "../../../../utility/apiInstance";
import ProfileCard from "../../common/profileCard";
import RecruiterSidebar from "../../common/recruiterSidebar";
import SettingsLinks from "../settingsLinks/settingsLinks";

const RecruiterCommissions = () => {
    const [commissions, setCommissions] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showWithdrawModal, setShowWithdrawModal] = useState(false);
    const [selectedCommission, setSelectedCommission] = useState<any>(null);
    const [submittingWithdraw, setSubmittingWithdraw] = useState(false);

    const [bankDetails, setBankDetails] = useState({
        accountHolderName: "",
        bankName: "",
        accountNumber: "",
        confirmAccountNumber: "",
        branchName: "",
        accountType: "",
        country: "",
        currency: "",
        swiftCode: "",
        ifscCode: "",
        routingNumber: "",
        iban: "",
        note: "",
    });

    const user = useSelector((state: any) => state.auth.user);

    useEffect(() => {
        const fetchCommissions = async () => {
            try {
                const response = await apiInstance.get("/api/applications", {
                    params: {
                        "where[student.agent][equals]": user.id,
                        "where[commissionAmount][exists]": true,
                        depth: 2,
                    },
                });
                setCommissions(response.data.docs);
            } catch (err) {
                console.error("Failed to fetch commissions:", err);
            } finally {
                setLoading(false);
            }
        };

        if (user?.id) fetchCommissions();
    }, [user?.id]);

    const handleOpenWithdrawModal = (commission: any) => {
        setSelectedCommission(commission);
        setBankDetails({
            accountHolderName: "",
            bankName: "",
            accountNumber: "",
            confirmAccountNumber: "",
            branchName: "",
            accountType: "",
            country: "",
            currency: "",
            swiftCode: "",
            ifscCode: "",
            routingNumber: "",
            iban: "",
            note: "",
        });
        setShowWithdrawModal(true);
    };

    const handleCloseWithdrawModal = () => {
        setShowWithdrawModal(false);
        setSelectedCommission(null);
        setSubmittingWithdraw(false);
    };

    const handleBankDetailsChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setBankDetails((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const isWithdrawFormValid =
        bankDetails.accountHolderName.trim() &&
        bankDetails.bankName.trim() &&
        bankDetails.accountNumber.trim() &&
        bankDetails.confirmAccountNumber.trim() &&
        bankDetails.accountNumber === bankDetails.confirmAccountNumber &&
        bankDetails.accountType.trim() &&
        bankDetails.country.trim() &&
        bankDetails.currency.trim();

    const handleWithdrawRequest = async () => {
        if (!selectedCommission?.id) return;
        if (!isWithdrawFormValid) return;

        try {
            setSubmittingWithdraw(true);

            await apiInstance.patch(`/api/applications/${selectedCommission.id}`, {
                withdrawalRequested: true,
                withdrawalStatus: "requested",
                bankDetails: {
                    accountHolderName: bankDetails.accountHolderName,
                    bankName: bankDetails.bankName,
                    accountNumber: bankDetails.accountNumber,
                    branchName: bankDetails.branchName,
                    accountType: bankDetails.accountType,
                    country: bankDetails.country,
                    currency: bankDetails.currency,
                    swiftCode: bankDetails.swiftCode,
                    ifscCode: bankDetails.ifscCode,
                    routingNumber: bankDetails.routingNumber,
                    iban: bankDetails.iban,
                    note: bankDetails.note,
                },
            });

            setCommissions((prev) =>
                prev.map((item) =>
                    item.id === selectedCommission.id
                        ? {
                            ...item,
                            withdrawalRequested: true,
                            withdrawalStatus: "requested",
                            bankDetails: {
                                accountHolderName: bankDetails.accountHolderName,
                                bankName: bankDetails.bankName,
                                accountNumber: bankDetails.accountNumber,
                                branchName: bankDetails.branchName,
                                accountType: bankDetails.accountType,
                                country: bankDetails.country,
                                currency: bankDetails.currency,
                                swiftCode: bankDetails.swiftCode,
                                ifscCode: bankDetails.ifscCode,
                                routingNumber: bankDetails.routingNumber,
                                iban: bankDetails.iban,
                                note: bankDetails.note,
                            },
                        }
                        : item
                )
            );

            handleCloseWithdrawModal();
        } catch (error) {
            console.error("Failed to submit withdrawal request:", error);
        } finally {
            setSubmittingWithdraw(false);
        }
    };


    const totalEarnings = commissions.reduce(
        (sum, app) => sum + (app.commissionAmount || 0),
        0
    );

    return (
        <>
            <div className="content">
                <div className="container">
                    <ProfileCard />
                    <div className="row">
                        <RecruiterSidebar />
                        <div className="col-lg-9">
                            <div className="mb-3">
                                <h5>Settings</h5>
                            </div>
                            <SettingsLinks />

                            <div className="card p-4 shadow-sm border-0 bg-light">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="mb-0 font-heading">
                                        <i className="isax isax-wallet me-2 text-success"></i>
                                        Commissions
                                    </h5>
                                    <h6 className="text-muted">
                                        Total Earnings:{" "}
                                        <span className="fw-bold">${totalEarnings}</span>
                                    </h6>
                                </div>

                                <div className="table-responsive">
                                    <table className="table table-striped align-middle">
                                        <thead className="table-dark">
                                            <tr>
                                                <th>Student</th>
                                                <th>Program</th>
                                                <th>Level</th>
                                                <th>School Name</th>
                                                <th>Amount ($)</th>
                                                <th>Status</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr>
                                                    <td colSpan={7} className="text-center py-4 text-muted">
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        Loading commissions...
                                                    </td>
                                                </tr>
                                            ) : commissions.map((c: any) => {
                                                const studentName = c.student?.firstName
                                                    ? `${c.student.firstName} ${c.student.lastName || ""}`.trim()
                                                    : "N/A";
                                                const programName = c.program?.programName || "N/A";
                                                const programLevel = c.program?.programLevel || "N/A";
                                                const schoolName = c.institution?.schoolName || "N/A";

                                                const isApproved = c.commissionStatus === "approved";
                                                const alreadyRequested = c.withdrawalStatus === "requested";

                                                return (
                                                    <tr key={c.id}>
                                                        <td>{studentName}</td>
                                                        <td>{programName}</td>
                                                        <td>{programLevel}</td>
                                                        <td>{schoolName}</td>
                                                        <td>${c.commissionAmount || 0}</td>
                                                        <td>
                                                            <span
                                                                className={`badge ${c.commissionStatus === "paid"
                                                                        ? "bg-success"
                                                                        : c.commissionStatus === "approved"
                                                                            ? "bg-info"
                                                                            : "bg-warning text-dark"
                                                                    }`}
                                                            >
                                                                {c.commissionStatus || "Pending"}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            {isApproved ? (
                                                                alreadyRequested ? (
                                                                    <span className="badge bg-secondary">
                                                                        Withdraw Requested
                                                                    </span>
                                                                ) : (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-primary"
                                                                        onClick={() => handleOpenWithdrawModal(c)}
                                                                    >
                                                                        Withdraw
                                                                    </button>
                                                                )
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showWithdrawModal && (
                <div
                    className="modal d-block"
                    tabIndex={-1}
                    style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                >
                    <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Withdraw Commission</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={handleCloseWithdrawModal}
                                ></button>
                            </div>

                            <div className="modal-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Account Holder Name *</label>
                                        <input
                                            type="text"
                                            name="accountHolderName"
                                            className="form-control"
                                            value={bankDetails.accountHolderName}
                                            onChange={handleBankDetailsChange}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">Bank Name *</label>
                                        <input
                                            type="text"
                                            name="bankName"
                                            className="form-control"
                                            value={bankDetails.bankName}
                                            onChange={handleBankDetailsChange}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">Account Number *</label>
                                        <input
                                            type="text"
                                            name="accountNumber"
                                            className="form-control"
                                            value={bankDetails.accountNumber}
                                            onChange={handleBankDetailsChange}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">Confirm Account Number *</label>
                                        <input
                                            type="text"
                                            name="confirmAccountNumber"
                                            className="form-control"
                                            value={bankDetails.confirmAccountNumber}
                                            onChange={handleBankDetailsChange}
                                        />
                                        {bankDetails.confirmAccountNumber &&
                                            bankDetails.accountNumber !== bankDetails.confirmAccountNumber && (
                                                <small className="text-danger">
                                                    Account numbers do not match.
                                                </small>
                                            )}
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">Branch Name</label>
                                        <input
                                            type="text"
                                            name="branchName"
                                            className="form-control"
                                            value={bankDetails.branchName}
                                            onChange={handleBankDetailsChange}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">Account Type *</label>
                                        <select
                                            name="accountType"
                                            className="form-select"
                                            value={bankDetails.accountType}
                                            onChange={handleBankDetailsChange}
                                        >
                                            <option value="">Select account type</option>
                                            <option value="savings">Savings</option>
                                            <option value="current">Current</option>
                                            <option value="checking">Checking</option>
                                            <option value="business">Business</option>
                                        </select>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">Country *</label>
                                        <input
                                            type="text"
                                            name="country"
                                            className="form-control"
                                            value={bankDetails.country}
                                            onChange={handleBankDetailsChange}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">Currency *</label>
                                        <input
                                            type="text"
                                            name="currency"
                                            className="form-control"
                                            placeholder="USD / INR / GBP"
                                            value={bankDetails.currency}
                                            onChange={handleBankDetailsChange}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">SWIFT Code</label>
                                        <input
                                            type="text"
                                            name="swiftCode"
                                            className="form-control"
                                            value={bankDetails.swiftCode}
                                            onChange={handleBankDetailsChange}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">IFSC Code</label>
                                        <input
                                            type="text"
                                            name="ifscCode"
                                            className="form-control"
                                            value={bankDetails.ifscCode}
                                            onChange={handleBankDetailsChange}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">Routing Number</label>
                                        <input
                                            type="text"
                                            name="routingNumber"
                                            className="form-control"
                                            value={bankDetails.routingNumber}
                                            onChange={handleBankDetailsChange}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">IBAN</label>
                                        <input
                                            type="text"
                                            name="iban"
                                            className="form-control"
                                            value={bankDetails.iban}
                                            onChange={handleBankDetailsChange}
                                        />
                                    </div>

                                    <div className="col-12">
                                        <label className="form-label">Additional Note</label>
                                        <textarea
                                            name="note"
                                            className="form-control"
                                            rows={3}
                                            value={bankDetails.note}
                                            onChange={handleBankDetailsChange}
                                            placeholder="Optional payout note"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleCloseWithdrawModal}
                                    disabled={submittingWithdraw}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleWithdrawRequest}
                                    disabled={!isWithdrawFormValid || submittingWithdraw}
                                >
                                    {submittingWithdraw ? "Submitting..." : "Submit Withdrawal Request"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default RecruiterCommissions;